import { observer } from 'mobx-react-lite'
import { useHistory, useLocation } from 'react-router'
import { Link } from 'react-router-dom'
import { Formik, Form } from 'formik'
import * as Yup from 'yup'
import { toast } from 'react-toastify'
import { Box, Text, Group, Button } from '@mantine/core'
import { IoFlash } from 'react-icons/io5'
import MyTextInput from '../../app/common/form/MyTextInput'
import MyTextArea from '../../app/common/form/MyTextArea'
import Breadcrumb from '../../app/common/Breadcrumb'
import { useStore } from '../../app/stores/store'
import { FirearmModelFormValues } from '../../app/models/firearmModel'

const validationSchema = Yup.object({
  make: Yup.string().required('Make is required').max(200, 'Max 200 characters'),
  model: Yup.string().required('Model is required').max(200, 'Max 200 characters'),
  platform: Yup.string().max(200),
  caliber: Yup.string().max(100),
  actionType: Yup.string().max(100),
  yearIntroduced: Yup.number().nullable().min(1800).max(2100),
  description: Yup.string().max(5000),
  coverImageUrl: Yup.string().url('Must be a valid URL').max(2000)
})

/** Draft shape passed from the wizard when user clicks "Skip wizard — view full form instead". */
interface WizardDraft {
  platform?: string;
  make?: string;
  caliber?: string;
  model?: string;
  yearIntroduced?: number | null;
  description?: string;
}

export default observer(function RegistryFullForm () {
  const { firearmModelStore } = useStore()
  const { createModel, loading } = firearmModelStore
  const history = useHistory()
  const location = useLocation()
  const isEdit = false
  const draft = (location.state as { draft?: WizardDraft } | undefined)?.draft
  const registryParams = new URLSearchParams(location.search)
  const returnToPostAfterCreate =
    registryParams.get('returnTo') === 'createPost' &&
    registryParams.get('linkedAfterCreate') === 'true'

  const initialValues: FirearmModelFormValues = {
    make: draft?.make ?? '',
    model: draft?.model ?? '',
    platform: draft?.platform ?? '',
    caliber: draft?.caliber ?? '',
    actionType: '',
    yearIntroduced: draft?.yearIntroduced ?? null,
    description: draft?.description ?? '',
    coverImageUrl: ''
  }

  const handleSubmit = async (values: FirearmModelFormValues) => {
    const payload = {
      ...values,
      platform: values.platform || undefined,
      caliber: values.caliber || undefined,
      actionType: values.actionType || undefined,
      description: values.description || undefined,
      coverImageUrl: values.coverImageUrl || undefined
    }
    const newId = await createModel(payload)
    if (newId) {
      toast.info('Submitted! Your content has been submitted and is being reviewed before publishing.', { autoClose: 6000 })
      if (returnToPostAfterCreate) {
        history.push(
          `/createPost?resumeDraft=true&linkedFirearmId=${encodeURIComponent(newId)}`
        )
      } else {
        history.push(`/registry/${newId}`)
      }
    }
  }

  return (
    <Box sx={{ maxWidth: 720, margin: '0 auto' }}>
      <Box mb="md" sx={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <Text
          component={Link}
          to="/registry"
          size="sm"
          sx={{ color: 'var(--gs-muted)', textDecoration: 'none', '&:hover': { color: 'var(--gs-text)' } }}
        >
          ← Back to Registry
        </Text>
        <Breadcrumb
          items={[
            { label: 'Registry', href: '/registry' },
            { label: isEdit ? 'Edit model' : 'Submit new model' }
          ]}
        />
      </Box>
      <Box
        sx={{
          background: 'var(--gs-surface)',
          border: '1px solid var(--gs-border)',
          borderRadius: 8,
          padding: 24
        }}
      >
        <Text sx={{ fontFamily: 'Oswald, sans-serif', fontWeight: 700, fontSize: 22, color: 'var(--gs-text)', marginBottom: 4 }}>
          {isEdit ? 'EDIT FIREARM MODEL' : 'SUBMIT A FIREARM MODEL'}
        </Text>
        <Text size="sm" sx={{ fontFamily: 'DM Sans, sans-serif', color: 'var(--gs-muted)', marginBottom: 24 }}>
          New entries are pending until approved by an administrator.
        </Text>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ isValid, dirty }) => (
            <Form>
              <MyTextInput name="make" placeholder="e.g. Glock" label="Make" />
              <MyTextInput name="model" placeholder="e.g. 17 Gen5" label="Model" />
              <MyTextInput name="platform" placeholder="e.g. Glock, AR-15" label="Platform" />
              <MyTextInput name="caliber" placeholder="e.g. 9mm" label="Caliber" />
              <MyTextInput name="actionType" placeholder="e.g. Semi-auto" label="Action type" />
              <MyTextInput
                name="yearIntroduced"
                type="number"
                placeholder="e.g. 2017"
                label="Year introduced"
              />
              <MyTextArea
                name="description"
                placeholder="Brief description (optional)"
                rows={4}
                label="Description"
              />
              <MyTextInput
                name="coverImageUrl"
                placeholder="Cover image URL (optional)"
                label="Cover image URL"
              />
              <Text size="sm" color="dimmed" mt="sm" mb="sm">
                Save the model first, then add tags.
              </Text>
              {/* AmmoScore reward hint */}
              <Box
                sx={{
                  marginTop: '1rem',
                  background: 'rgba(201,168,76,0.08)',
                  border: '1px solid rgba(201,168,76,0.25)',
                  borderRadius: 6,
                  padding: '8px 14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8
                }}
              >
                <IoFlash size={13} color="#C9A84C" />
                <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 12, color: 'var(--gs-muted)' }}>
                  {isEdit
                    ? 'Keeping the Registry accurate earns '
                    : 'Submitting a new model earns '}
                  <strong style={{ color: '#C9A84C' }}>AmmoScore</strong>
                  {!isEdit && ' once a mod approves it.'}
                </span>
              </Box>
              <Group mt="xl">
                <Button
                  type="submit"
                  loading={loading}
                  disabled={!isValid}
                  sx={{ background: 'var(--gs-accent)', color: '#fff', '&:hover': { background: '#bf4a00' } }}
                >
                  {isEdit ? 'Save' : 'Submit for approval'}
                </Button>
                <Button component={Link} to="/registry" variant="default">
                  Cancel
                </Button>
              </Group>
            </Form>
          )}
        </Formik>
      </Box>
    </Box>
  )
})
